/**
 * Accounting Services › Reconciliations: a workpaper per bank, credit card and loan account and
 * month. The statement balance plus reconciling items is compared with the book balance from
 * QuickBooks; the preparer signs at a zero difference, then someone else reviews.
 */
import { formatCents } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, CircleDashed, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { MoneyInput, SignedMoney } from "@/components/bookkeeping";
import { ask } from "@/components/dialogs";
import { Empty, Loading } from "@/components/page";
import { UploadButton } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime, formatPeriod } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";

type Item = { id?: string; kind: "deposit_in_transit" | "outstanding_payment" | "other"; date: string; description: string; cents: number; txnId: string | null };
type Workpaper = {
  id: string;
  accountQboId: string;
  accountName: string;
  accountType: string;
  period: string;
  statementDate: string;
  statementBalanceCents: number;
  bookBalanceCents: number | null;
  bookBalanceAt: string | null;
  items: Item[];
  notes: string;
  itemsTotal: number;
  adjustedStatementCents: number;
  differenceCents: number | null;
  preparedAt: string | null;
  preparedBy: string | null;
  preparedByUserId: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  statement: { id: string; filename: string } | null;
};
type AccountRow = { account: { qboId: string; name: string; type: string }; monthly: boolean; workpaper: Workpaper | null };
type Data = { period: string; end: string; rows: AccountRow[]; optional: Array<{ qboId: string; name: string; type: string }> };
type Activity = { date: string; type: string; num: string; name: string; memo: string; txnId: string | null; cents: number };

const field = "h-9 rounded-md border border-input bg-background px-2 text-sm";
const ITEM_LABEL: Record<Item["kind"], string> = { deposit_in_transit: "Deposit in transit", outstanding_payment: "Outstanding payment", other: "Other reconciling item" };

function lastMonths(n: number) {
  const d = new Date();
  return Array.from({ length: n }, (_, i) => new Date(d.getFullYear(), d.getMonth() - 1 - i, 15).toLocaleDateString("en-CA").slice(0, 7));
}

function status(w: Workpaper | null): [string, string] {
  if (!w) return ["Not started", "bg-muted text-muted-foreground"];
  if (w.reviewedAt) return ["Reviewed", "bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200"];
  if (w.preparedAt) return ["Awaiting review", "bg-violet-100 text-violet-900 dark:bg-violet-900/30 dark:text-violet-200"];
  if (w.differenceCents === 0) return ["Ready to sign", "bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-200"];
  return ["Difference", "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200"];
}

export function Reconciliations({ clientId, me }: { clientId: string; me: Me }) {
  const months = lastMonths(12);
  const [period, setPeriod] = useState(months[0]!);
  const { data, isLoading, error } = useQuery<Data>({ queryKey: [`/api/clients/${clientId}/books/reconciliations?period=${period}`], retry: false });
  const [open, setOpen] = useState<{ account: AccountRow["account"]; workpaper: Workpaper | null } | null>(null);
  const done = data?.rows.filter((r) => r.workpaper?.reviewedAt).length ?? 0;
  const monthly = data?.rows.filter((r) => r.monthly).length ?? 0;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <label className="text-xs text-muted-foreground">
          <span className="mb-1 block">Month</span>
          <select aria-label="Month" className={field} value={period} onChange={(e) => setPeriod(e.target.value)}>
            {months.map((m) => (
              <option key={m} value={m}>
                {formatPeriod(m)}
              </option>
            ))}
          </select>
        </label>
        {data && (
          <p className="text-sm text-muted-foreground">
            {done} of {monthly} bank and card account{monthly === 1 ? "" : "s"} reconciled and reviewed
          </p>
        )}
      </div>
      {isLoading ? (
        <Loading />
      ) : error || !data ? (
        <Empty>{errorMessage(error)}</Empty>
      ) : !data.rows.length && !data.optional.length ? (
        <Empty>No bank, credit card or loan accounts in QuickBooks. Refresh the chart of accounts under Overview.</Empty>
      ) : (
        <>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {data.rows.map((r) => {
              const w = r.workpaper;
              const [label, tone] = status(w);
              return (
                <li key={r.account.qboId}>
                  <button type="button" onClick={() => setOpen({ account: r.account, workpaper: w })} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-3 py-3 text-left text-sm hover:bg-muted/50">
                    {w?.reviewedAt ? <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> : <CircleDashed className="h-4 w-4 shrink-0 text-muted-foreground" />}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{r.account.name}</span>
                      <span className="block text-xs text-muted-foreground">
                        {r.account.type}
                        {w && ` · statement ${formatDate(w.statementDate)}`}
                        {w?.preparedBy && ` · prepared by ${w.preparedBy}`}
                        {w?.reviewedBy && ` · reviewed by ${w.reviewedBy}`}
                      </span>
                    </span>
                    {w && (
                      <span className="tabular text-right text-xs text-muted-foreground">
                        Statement {formatCents(w.statementBalanceCents)}
                        <span className="block">Books {w.bookBalanceCents === null ? "—" : formatCents(w.bookBalanceCents)}</span>
                      </span>
                    )}
                    {w && w.differenceCents !== 0 && w.differenceCents !== null && <span className="tabular text-sm font-medium text-amber-700 dark:text-amber-300">Off by {formatCents(Math.abs(w.differenceCents))}</span>}
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", tone)}>{label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {data.optional.length > 0 && hasPermission(me, "engagements.write") && (
            <label className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              Reconcile a loan or other liability this month:
              <select
                aria-label="Add a loan account"
                className={field}
                value=""
                onChange={(e) => {
                  const a = data.optional.find((x) => x.qboId === e.target.value);
                  if (a) setOpen({ account: a, workpaper: null });
                }}
              >
                <option value="">Choose an account</option>
                {data.optional.map((a) => (
                  <option key={a.qboId} value={a.qboId}>
                    {a.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <p className="text-xs text-muted-foreground">QuickBooks doesn't let outside software press “Reconcile”. Once a workpaper is reviewed here, complete the reconciliation in QuickBooks with the same statement balance.</p>
        </>
      )}
      {open && data && <WorkpaperDialog clientId={clientId} me={me} period={data.period} periodEnd={data.end} account={open.account} initial={open.workpaper} onClose={() => setOpen(null)} />}
    </div>
  );
}

function WorkpaperDialog({ clientId, me, period, periodEnd, account, initial, onClose }: { clientId: string; me: Me; period: string; periodEnd: string; account: AccountRow["account"]; initial: Workpaper | null; onClose: () => void }) {
  const { toast } = useToast();
  const canEdit = hasPermission(me, "engagements.write");
  const [w, setW] = useState<Workpaper | null>(initial);
  const [date, setDate] = useState(initial?.statementDate ?? periodEnd);
  const [balance, setBalance] = useState(initial?.statementBalanceCents ?? 0);
  const [items, setItems] = useState<Item[]>(initial?.items ?? []);
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [picking, setPicking] = useState(false);
  const dirty = !w || date !== w.statementDate || balance !== w.statementBalanceCents || notes !== w.notes || JSON.stringify(items) !== JSON.stringify(w.items);
  const signed = !!w?.preparedAt;
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<Workpaper>("PUT", `/api/clients/${clientId}/books/reconciliations`, { accountQboId: account.qboId, period, statementDate: date, statementBalanceCents: balance, items, notes }),
    onSuccess: (r) => {
      setW(r);
      setItems(r.items);
    },
  });
  const upload = useMutation({ meta: { inlineError: true }, mutationFn: (f: File) => api.upload<Workpaper>(`/api/reconciliations/${w!.id}/statement`, {}, f), onSuccess: setW });
  const sign = useMutation({
    meta: { inlineError: true },
    mutationFn: (as: "preparer" | "reviewer") => api.send<Workpaper>("POST", `/api/reconciliations/${w!.id}/sign`, { as }),
    onSuccess: (r) => {
      setW(r);
      toast({ title: r.reviewedAt ? "Reconciliation reviewed" : "Signed as preparer", description: r.reviewedAt ? undefined : "Another team member reviews it next." });
    },
  });
  const reopen = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send<Workpaper>("POST", `/api/reconciliations/${w!.id}/reopen`, {}), onSuccess: setW });
  const itemsTotal = items.reduce((a, i) => a + i.cents, 0);
  const adjusted = balance + itemsTotal;
  const difference = w?.bookBalanceCents === null || w?.bookBalanceCents === undefined ? null : w.bookBalanceCents - adjusted;
  const err = save.error ?? upload.error ?? sign.error ?? reopen.error;
  const ro = !canEdit || signed;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {account.name} · {formatPeriod(period)}
          </DialogTitle>
          <DialogDescription>
            Enter the statement's ending balance and list what is in the books but not yet on the statement. The book balance is read from QuickBooks as of the statement date each time you save.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">Statement date</span>
            <input type="date" disabled={ro} className={cn(field, "w-full")} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">Statement ending balance{account.type !== "Bank" ? " (amount owed)" : ""}</span>
            <MoneyInput label="Statement ending balance" disabled={ro} value={balance} onChange={setBalance} />
          </label>
        </div>

        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium">Reconciling items</p>
            {!ro && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={!w} title={w ? undefined : "Save the workpaper first"} onClick={() => setPicking(true)}>
                  Pick from account activity
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setItems([...items, { kind: "other", date, description: "", cents: 0, txnId: null }])}>
                  <Plus className="h-3.5 w-3.5" /> Add item
                </Button>
              </div>
            )}
          </div>
          {!items.length ? (
            <p className="text-sm text-muted-foreground">No reconciling items. If the statement agrees with the books, the difference below is zero.</p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {items.map((it, i) => (
                <li key={it.id ?? `new-${i}`} className="grid grid-cols-2 gap-2 px-2 py-2 sm:grid-cols-[11.5rem_9rem_minmax(0,1fr)_8.5rem_2rem] sm:items-center">
                  <select aria-label="Item type" disabled={ro} className={field} value={it.kind} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, kind: e.target.value as Item["kind"] } : x)))}>
                    {(Object.keys(ITEM_LABEL) as Item["kind"][]).map((k) => (
                      <option key={k} value={k}>
                        {ITEM_LABEL[k]}
                      </option>
                    ))}
                  </select>
                  <input type="date" aria-label="Item date" disabled={ro} className={field} value={it.date} onChange={(e) => e.target.value && setItems(items.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} />
                  <Input aria-label="Item description" disabled={ro} className="col-span-2 sm:col-span-1" value={it.description} onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} />
                  <SignedMoney disabled={ro} value={it.cents} onChange={(c) => setItems(items.map((x, j) => (j === i ? { ...x, cents: c } : x)))} />
                  {!ro && (
                    <button type="button" aria-label="Remove item" onClick={() => setItems(items.filter((_, j) => j !== i))} className="flex justify-center text-muted-foreground hover:text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="text-xs text-muted-foreground">Amounts are signed by their effect on the books: a deposit in transit is positive, an outstanding check or payment negative.</p>
        </div>

        <dl className="grid gap-x-6 gap-y-1 rounded-lg border border-border bg-secondary/40 p-3 text-sm sm:grid-cols-2">
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Statement balance</dt>
            <dd className="tabular">{formatCents(balance)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Reconciling items</dt>
            <dd className="tabular">{formatCents(itemsTotal)}</dd>
          </div>
          <div className="flex justify-between font-medium">
            <dt>Adjusted statement balance</dt>
            <dd className="tabular">{formatCents(adjusted)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-muted-foreground">Balance in QuickBooks{w?.bookBalanceAt ? ` (read ${formatDateTime(w.bookBalanceAt)})` : ""}</dt>
            <dd className="tabular">{w?.bookBalanceCents === null || !w ? "Save to read" : formatCents(w.bookBalanceCents)}</dd>
          </div>
          <div className={cn("flex justify-between font-semibold sm:col-span-2", difference === 0 ? "text-emerald-700 dark:text-emerald-300" : difference !== null ? "text-amber-700 dark:text-amber-300" : "")}>
            <dt>Difference</dt>
            <dd className="tabular">{difference === null ? "—" : formatCents(difference)}</dd>
          </div>
        </dl>
        {dirty && w && <p className="text-xs text-amber-700 dark:text-amber-300">Unsaved changes: save to compare with QuickBooks again.</p>}

        <label className="text-xs text-muted-foreground">
          <span className="mb-1 block">Notes for the reviewer</span>
          <Textarea rows={2} disabled={ro} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          {w?.statement ? (
            <a href={`/api/documents/${w.statement.id}/download`} className="text-primary hover:underline">
              {w.statement.filename}
            </a>
          ) : (
            <span className="text-muted-foreground">No statement attached.</span>
          )}
          {canEdit && w && !signed && <UploadButton label={w.statement ? "Replace statement" : "Attach statement"} disabled={upload.isPending} onFile={(f) => upload.mutate(f)} />}
        </div>
        {w?.preparedAt && (
          <p className="text-xs text-muted-foreground">
            Prepared by {w.preparedBy} on {formatDateTime(w.preparedAt)}
            {w.reviewedAt && ` · reviewed by ${w.reviewedBy} on ${formatDateTime(w.reviewedAt)}`}
          </p>
        )}
        {err && <p className="text-sm text-destructive">{errorMessage(err)}</p>}
        <DialogFooter className="flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {canEdit && signed && (
            <Button variant="outline" onClick={async () => (await ask({ title: "Reopen this workpaper?", description: "Both sign-offs are cleared so it can be corrected.", confirmLabel: "Reopen" })) && reopen.mutate()}>
              Reopen
            </Button>
          )}
          {canEdit && !signed && (
            <Button variant={dirty ? "default" : "outline"} onClick={() => save.mutate()} disabled={save.isPending || !dirty || items.some((i) => !i.description.trim() || i.cents === 0)}>
              {save.isPending ? "Reading QuickBooks…" : "Save and compare"}
            </Button>
          )}
          {canEdit && w && !signed && (
            <Button onClick={() => sign.mutate("preparer")} disabled={dirty || w.differenceCents !== 0 || !w.statement || sign.isPending} title={!w.statement ? "Attach the statement first" : w.differenceCents !== 0 ? "The difference must be zero" : undefined}>
              Sign as preparer
            </Button>
          )}
          {canEdit && w?.preparedAt && !w.reviewedAt && (
            <Button onClick={() => sign.mutate("reviewer")} disabled={sign.isPending || w.preparedByUserId === me.user?.id} title={w.preparedByUserId === me.user?.id ? "Someone other than the preparer reviews" : undefined}>
              Sign as reviewer
            </Button>
          )}
        </DialogFooter>
        {picking && w && (
          <ActivityPicker
            workpaperId={w.id}
            taken={new Set(items.map((i) => i.txnId).filter((x): x is string => !!x))}
            onPick={(rows) => {
              setItems([...items, ...rows.map((r): Item => ({ kind: r.cents > 0 ? "deposit_in_transit" : "outstanding_payment", date: r.date, description: [r.type, r.num && `#${r.num}`, r.name].filter(Boolean).join(" · ").slice(0, 300), cents: r.cents, txnId: r.txnId }))]);
              setPicking(false);
            }}
            onClose={() => setPicking(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ActivityPicker({ workpaperId, taken, onPick, onClose }: { workpaperId: string; taken: Set<string>; onPick: (rows: Activity[]) => void; onClose: () => void }) {
  const { data, isLoading, error } = useQuery<Activity[]>({ queryKey: [`/api/reconciliations/${workpaperId}/activity`], retry: false });
  const [sel, setSel] = useState<Set<number>>(new Set());
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Uncleared items</DialogTitle>
          <DialogDescription>The account's activity in the six weeks to the statement date. Select what isn't on the statement yet.</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <Loading />
        ) : error || !data ? (
          <Empty>{errorMessage(error)}</Empty>
        ) : !data.length ? (
          <Empty>No activity in this account in the six weeks to the statement date.</Empty>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border text-sm">
            {data.map((r, i) => {
              const used = !!r.txnId && taken.has(r.txnId);
              return (
                <li key={i}>
                  <label className={cn("flex items-center gap-3 px-3 py-2", used && "opacity-50")}>
                    <input type="checkbox" disabled={used} checked={sel.has(i)} onChange={(e) => setSel((s) => (e.target.checked ? new Set([...s, i]) : new Set([...s].filter((x) => x !== i))))} />
                    <span className="tabular w-24 shrink-0">{formatDate(r.date)}</span>
                    <span className="min-w-0 flex-1 truncate">
                      {r.type}
                      {r.num && ` #${r.num}`} · {r.name || r.memo || "—"}
                    </span>
                    <span className="tabular">{formatCents(r.cents)}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!sel.size} onClick={() => onPick([...sel].map((i) => data![i]!))}>
            Add {sel.size || ""} item{sel.size === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
